import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings as SettingsIcon, Shield, Bell, Loader, Check, X,
  ArrowLeft, AlertTriangle, Mail, User, Sliders, Sparkles
} from 'lucide-react';
import { authService, profileService } from '../services/api';
import { useAuth } from '../context/AuthContext';

function Settings() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(false);
  
  // Reminder settings
  const [reminderSettings, setReminderSettings] = useState({
    reminder_enabled: false,
    reminder_frequency: 'weekly',
    reminder_day: 1,
  });
  
  // Professional profile
  const [profile, setProfile] = useState({
    full_name: '',
    employee_id: '',
    department: '',
    cost_center: '',
    phone: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);

  // App preferences
  const [ocrEnabled, setOcrEnabled] = useState(true);
  const [ocrSaving, setOcrSaving] = useState(false);

  // 2FA state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable2FA, setShowDisable2FA] = useState(false);

  useEffect(() => {
    if (user) {
      setReminderSettings({
        reminder_enabled: user.reminder_enabled || false,
        reminder_frequency: user.reminder_frequency || 'weekly',
        reminder_day: user.reminder_day || 1,
      });
      setOcrEnabled(user.ocr_enabled !== false); // default true
    }
    checkEmailStatus();
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    try {
      const data = await profileService.getProfile();
      setProfile({
        full_name: data.full_name || '',
        employee_id: data.employee_id || '',
        department: data.department || '',
        cost_center: data.cost_center || '',
        phone: data.phone || '',
      });
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const handleSaveProfile = async () => {
    setProfileLoading(true);
    setError('');
    setSuccess('');
    try {
      await profileService.updateProfile(profile);
      setSuccess('Professional profile saved');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const checkEmailStatus = async () => {
    try {
      const status = await authService.getEmailStatus();
      setEmailEnabled(status.email_enabled);
    } catch (err) {
      console.error('Error checking email status:', err);
    }
  };

  const handleToggleOcr = async (newValue) => {
    setOcrEnabled(newValue);
    setOcrSaving(true);
    setError('');
    try {
      await authService.updateSettings({ ocr_enabled: newValue });
      if (refreshUser) refreshUser();
    } catch (err) {
      setOcrEnabled(!newValue); // revert on failure
      setError(err.response?.data?.detail || 'Error saving preference');
    } finally {
      setOcrSaving(false);
    }
  };

  const handleSaveReminders = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await authService.updateSettings(reminderSettings);
      setSuccess('Reminder settings saved');
      if (refreshUser) refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await authService.setup2FA();
      setTwoFAData(data);
      setShow2FASetup(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error setting up 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setLoading(true);
    setError('');
    
    try {
      await authService.verify2FA(verifyCode);
      setSuccess('Two-factor authentication enabled');
      setShow2FASetup(false);
      setTwoFAData(null);
      setVerifyCode('');
      if (refreshUser) refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    setError('');
    
    try {
      await authService.disable2FA(disableCode);
      setSuccess('Two-factor authentication disabled');
      setShowDisable2FA(false);
      setDisableCode('');
      if (refreshUser) refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const dayOptions = {
    weekly: [
      { value: 0, label: 'Monday' },
      { value: 1, label: 'Tuesday' },
      { value: 2, label: 'Wednesday' },
      { value: 3, label: 'Thursday' },
      { value: 4, label: 'Friday' },
      { value: 5, label: 'Saturday' },
      { value: 6, label: 'Sunday' },
    ],
    monthly: Array.from({ length: 28 }, (_, i) => ({ 
      value: i + 1, 
      label: `Day ${i + 1}` 
    })),
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header (mobile shows the title in the top bar) */}
      <div className="hidden md:flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <SettingsIcon className="w-8 h-8 text-gray-600" />
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        </div>
      </div>

      {/* Success/Error messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          <p className="text-green-700">{success}</p>
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <X className="w-5 h-5 text-red-600" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* App Preferences */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Sliders className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-semibold">App Preferences</h2>
        </div>
        <div className="flex items-center justify-between py-3">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-900">Receipt OCR</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Automatically scan uploaded receipts to suggest amount, date, and merchant.
                Runs locally on your device — no data is sent to any server.
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={ocrSaving}
            onClick={() => handleToggleOcr(!ocrEnabled)}
            className={`relative ml-6 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 ${
              ocrEnabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            role="switch"
            aria-checked={ocrEnabled}
            aria-label="Toggle receipt OCR"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                ocrEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
        </div>
        
        {user?.two_factor_enabled ? (
          <div>
            <div className="flex items-center gap-2 mb-4 text-green-600">
              <Check className="w-5 h-5" />
              <span>2FA is enabled</span>
            </div>
            
            {showDisable2FA ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Enter your authenticator code to disable 2FA:
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-center text-xl tracking-widest"
                  placeholder="000000"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleDisable2FA}
                    disabled={loading || disableCode.length !== 6}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white py-2 rounded-lg"
                  >
                    {loading ? 'Disabling...' : 'Disable 2FA'}
                  </button>
                  <button
                    onClick={() => setShowDisable2FA(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowDisable2FA(true)}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                Disable 2FA
              </button>
            )}
          </div>
        ) : show2FASetup && twoFAData ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
            </p>
            <div className="flex justify-center">
              <img src={twoFAData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Manual entry code:</p>
              <code className="text-sm font-mono break-all">{twoFAData.secret}</code>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter the 6-digit code from your app:
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-center text-xl tracking-widest"
                placeholder="000000"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleVerify2FA}
                disabled={loading || verifyCode.length !== 6}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 rounded-lg"
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
              <button
                onClick={() => {
                  setShow2FASetup(false);
                  setTwoFAData(null);
                  setVerifyCode('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Add an extra layer of security to your account by enabling two-factor authentication.
            </p>
            <button
              onClick={handleSetup2FA}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              Set up 2FA
            </button>
          </div>
        )}
      </div>

      {/* Email Reminders */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-semibold">Email Reminders</h2>
        </div>
        
        {!emailEnabled && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <p className="text-sm text-yellow-700">
              Email is not configured on this server. Reminders won't be sent.
            </p>
          </div>
        )}
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={reminderSettings.reminder_enabled}
              onChange={(e) => setReminderSettings(prev => ({
                ...prev,
                reminder_enabled: e.target.checked
              }))}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">Enable expense reminders</span>
          </label>
          
          {reminderSettings.reminder_enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reminder Frequency
                </label>
                <select
                  value={reminderSettings.reminder_frequency}
                  onChange={(e) => setReminderSettings(prev => ({
                    ...prev,
                    reminder_frequency: e.target.value,
                    reminder_day: e.target.value === 'weekly' ? 1 : (e.target.value === 'monthly' ? 1 : 0)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              
              {reminderSettings.reminder_frequency !== 'daily' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {reminderSettings.reminder_frequency === 'weekly' ? 'Day of Week' : 'Day of Month'}
                  </label>
                  <select
                    value={reminderSettings.reminder_day}
                    onChange={(e) => setReminderSettings(prev => ({
                      ...prev,
                      reminder_day: parseInt(e.target.value)
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {dayOptions[reminderSettings.reminder_frequency]?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <p className="text-sm text-gray-500">
                <Mail className="w-4 h-4 inline mr-1" />
                Reminders are sent at 9:00 AM with a summary of your pending expenses.
              </p>
            </>
          )}
          
          <button
            onClick={handleSaveReminders}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Reminder Settings
          </button>
        </div>
      </div>

      {/* Professional Profile */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <User className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-semibold">Professional Profile</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: 'full_name', label: 'Full Name', hint: 'Used in PDF exports instead of username' },
            { key: 'employee_id', label: 'Employee ID' },
            { key: 'department', label: 'Department' },
            { key: 'cost_center', label: 'Cost Center' },
            { key: 'phone', label: 'Phone' },
          ].map(({ key, label, hint }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <input
                type="text"
                value={profile[key]}
                onChange={e => setProfile(p => ({ ...p, [key]: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
            </div>
          ))}
        </div>
        <button
          onClick={handleSaveProfile}
          disabled={profileLoading}
          className="mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          {profileLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save Profile
        </button>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Account Information</h2>
        <div className="space-y-2 text-sm">
          <p><span className="text-gray-500">Username:</span> {user?.username}</p>
          <p><span className="text-gray-500">Email:</span> {user?.email}</p>
          <p><span className="text-gray-500">Member since:</span> {user?.created_at && new Date(user.created_at).toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
}

export default Settings;
